// core
import React, { ReactElement, useContext } from "react";
import { QuoteGroupContext } from "../../../../compare/providers/RootContextProvider";

// components
import LAccordion from "../../../../compare/components/LAccordion/LAccordion";
import Icon from "../../Icon/Icon";
import NumberFormat from "react-number-format";
import QuoteBreakdownSummaryHeader from "../QuoteBreakdownSummaryHeader/QuoteBreakdownSummaryHeader";
import QuoteBreakdownClientBenefits from "../QuoteBreakdownClientBenefits/QuoteBreakdownClientBenefits";
import QuoteBreakdownSummaryFooter from "../QuoteBreakdownSummaryFooter/QuoteBreakdownSummaryFooter";
import QuoteBreakdownAddOn from "../QuoteBreakdownAddOn/QuoteBreakdownAddOn";

// helpers
import { generateClientApplyResultString } from "../../../shared/clientHelpers";
import { getProductTypeListAsString } from "../../../shared/enumHelpers";

// interfaces
import { IClient } from "../../../interfaces/IClient";
import { IBenefitResult } from "../../../interfaces/IBenefitResult";
import { PaymentFrequency } from "../../../interfaces/enums/PaymentFrequency";
import { QuoteSettingContainerType } from "../../../interfaces/settings/QuoteSettingContainerType";
import ProductType, {
    ClientProductTypesList,
    isClientProductType
} from "../../../interfaces/enums/ProductType";
import { IQuoteSettingContainerWaiverOfPremium } from "../../../interfaces/settings/IQuoteSettingsContainerWaiverOfPremium";

// hooks
import useAccordion from "../../../hooks/useAccordion";

// styles
import cx from "classnames";
import styles from "./QuoteBreakdownSummary.module.scss";
import { BenefitType } from "../../../interfaces/enums/BenefitType";



interface IQuoteBreakdownSummaryProps {
    providerId: number;
    clients: IClient[];
    settings: QuoteSettingContainerType[];
    benefits: IBenefitResult[];
    totalDiscount?: number;
    totalPremium?: number;
    totalCashback?: number;

    size?: "small" | "large";
    displayError?: string;
    className?: string;
    isCard: boolean;
}

const QuoteBreakdownSummary: React.FC<IQuoteBreakdownSummaryProps> = (props) => {
    const {
        providerId,
        totalDiscount,
        totalPremium,
        totalCashback,
        displayError,
        className,
        benefits,
        isCard
    } = props;

    const settings = [...props.settings].sort((a, b) => {
        if (!isClientProductType(a.productType)) return -1;
        if (!isClientProductType(b.productType)) return 1;

        return (
            ClientProductTypesList.indexOf(a.productType) -
            ClientProductTypesList.indexOf(b.productType)
        );
    });

    // Adults should be displayed first
    const clients = [...props.clients].sort(
        (a, b) => (b.isChild ? 0 : 1) - (a.isChild ? 0 : 1)
    );

    const paymentFrequency = settings[0]?.paymentFrequency || PaymentFrequency.Unknown;

    const size = props.size || "large";

    const { sectionIsOpen, handleChangeSection } = useAccordion(undefined, false);

    const getBenefits = (personId: number, caseId = 0): IBenefitResult[] => {
        return benefits.filter(
            (s) => s.personId === personId && (s.caseId === caseId || caseId === 0)
        );
    };

    const benefitCases = benefits.map((s) => s.caseId);
    const settingsToShow = settings.filter((s) => benefitCases.includes(s.caseId));

    const baseWOPSetting = settingsToShow.find(
        (s) =>
            s.productType === ProductType.Income || s.productType === ProductType.Mortgage
    );

    if (baseWOPSetting) {
        const waiverOfPremiumSettings: IQuoteSettingContainerWaiverOfPremium = {
            versionId: baseWOPSetting.versionId,
            productType: ProductType.WaiverOfPremium,
            caseId: 0,
            paymentFrequency,
            allowChildren: false,
            addons: [],
            clients: [],
            isLatestVersion: true,
            sortBy: baseWOPSetting.sortBy
        };

        if (baseWOPSetting.productType === ProductType.Mortgage) {
            waiverOfPremiumSettings.clients = baseWOPSetting.clients;
        } else if (baseWOPSetting.productType === ProductType.Income) {
            waiverOfPremiumSettings.clients = baseWOPSetting.clients;
        }

        settingsToShow.push(waiverOfPremiumSettings);
    }

    //Provider Add-ons
    const { selectors } = useContext(QuoteGroupContext);
    const providerAddOns = selectors.getProviderAddOns();
    const selectedAddOns = selectors.getSelectedAddOns(providerId);

    return (
        <div
            className={cx(styles.container, {
                [className]: !!className,
                loading: !benefits,
                [styles.card]: isCard
            })}>
            <QuoteBreakdownSummaryHeader
                providerId={providerId}
            />
            <h2 className={"text-card-title"}>
                {getProductTypeListAsString(settings.map((s) => s.productType))}
            </h2>
            <div
                className={cx({
                    [styles["result-summary--small"]]: size === "small"
                })}>
                {clients.map((client) => {
                    const clientId = client.personId.toString();

                    const clientBenefits = getBenefits(client.personId);

                    const totalClientPremium = clientBenefits
                        .map((s) => s.discountedPremium)
                        .reduce((a, b) => a + b, 0);

                    return (
                        <LAccordion
                            key={clientId}
                            name={clientId}
                            expanded={sectionIsOpen(clientId)}
                            handleChange={handleChangeSection}
                            size="small"
                            headerAlign="top"
                            summary={(): ReactElement => (
                                <div
                                    className={cx([
                                        "layout-flex-row--justify-spaced",
                                        "text-font-size-s",
                                        "text-line-height-m",
                                        "layout-flex-s"
                                    ])}>
                                    <div
                                        className={cx([
                                            styles.details,
                                            "layout-flex-row--centered"
                                        ])}>
                                        <a className={cx("space-l")}>Details</a>
                                        <Icon
                                            id="YouAndFamily"
                                            size="l"
                                            className="space-s"
                                        />
                                        {generateClientApplyResultString(client)}
                                    </div>
                                    <div>
                                        {clientBenefits.length <= 0 ? (
                                            "-"
                                        ) : (
                                            <NumberFormat
                                                value={totalClientPremium}
                                                prefix={"$"}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                isNumericString={true}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}>
                            {settingsToShow.map((s) => {
                                return (
                                    <QuoteBreakdownClientBenefits
                                        key={s.productType}
                                        setting={s}
                                        benefits={clientBenefits.filter(
                                            (b) => b.caseId === s.caseId
                                        )}
                                    />
                                );
                            })}
                        </LAccordion>
                    );
                })}
            </div>
            {selectedAddOns.map((s) => {
                const providerAddOn = providerAddOns.find((p) => p.systemName === s.providerAddOnSysName);
                if (!providerAddOn || s.discountAmount <= 0) return;

                return (
                    <React.Fragment key={s.providerAddOnSysName}>
                        <h2 className={"text-card-title"} key={s.providerAddOnSysName}>
                            {providerAddOn.addOnName}
                        </h2>
                        {clients.map((client) => {
                            const clientId = client.personId.toString() + providerAddOn.addOnName;

                            const clientBenefits = getBenefits(client.personId);

                            let totalClientPremium = clientBenefits
                                .map((s) => s.discountedPremium)
                                .reduce((a, b) => a + b, 0);

                            if (s.providerAddOnSysName === "LifetimeReward") {
                                totalClientPremium = clientBenefits
                                    .filter((s) => s.benefitType === BenefitType.LifeCover || s.benefitType === BenefitType.TraumaCover)
                                    .map((s) => s.discountedPremium)
                                    .reduce((a, b) => a + b, 0);
                            }

                            const addOnPremium = totalClientPremium * -1 * providerAddOn.discountPercentage / 100

                            return (
                                <LAccordion
                                    key={clientId}
                                    name={clientId}
                                    expanded={sectionIsOpen(clientId)}
                                    handleChange={handleChangeSection}
                                    size="small"
                                    headerAlign="top"
                                    summary={(): ReactElement => (
                                        <div
                                            className={cx([
                                                "layout-flex-row--justify-spaced",
                                                "text-font-size-s",
                                                "text-line-height-m",
                                                "layout-flex-s"
                                            ])}>
                                            <div
                                                className={cx([
                                                    styles.details,
                                                    "layout-flex-row--centered"
                                                ])}>
                                                <a className={cx("space-l")}>Details</a>
                                                <Icon
                                                    id="YouAndFamily"
                                                    size="l"
                                                    className="space-s"
                                                />
                                                {generateClientApplyResultString(client)}
                                            </div>
                                            <NumberFormat
                                                value={!client.isChild ? addOnPremium : 0}
                                            prefix={"$"}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            isNumericString={true}
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                                className={cx({
                                                    ["text-brand"]: !client.isChild && addOnPremium < 0
                                            })}
                                        /> 
                                        </div>
                                    )}>                                    
                                    <QuoteBreakdownAddOn
                                        key={clientId}
                                        providerAddOn={providerAddOn}
                                        addOnPremium={addOnPremium}
                                        isChild={client.isChild}
                                    />                                
                                </LAccordion>
                            );
                        })}
                    </React.Fragment>
                );
            })}
            <QuoteBreakdownSummaryFooter
                totalDiscount={totalDiscount}
                paymentFrequency={paymentFrequency}
                providerId={providerId}
                totalPremium={totalPremium}
                totalCashback={totalCashback}
                displayError={displayError}
            />

            {props.children && <div>{props.children}</div>}
        </div>
    );
};

export default QuoteBreakdownSummary;
